import { createContext, useMemo } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  NotesSummariesResponseSchema,
  noteTargetKey,
  type NoteTarget,
  type NotesSummariesRequest,
} from '@shared/flow/notes';
import { useApi } from '../../../../lib/api';
import { fetchJson } from '../../../../lib/apiFetchJson';
import { useNotesEnabled } from '../../../../hooks/useNotesEnabled';
import { FLOW_NOTES_SUMMARIES_KEY } from './notes-query-keys';

/** What a row/header shows for one target. `unread` is the server's `unread > 0`; never recomputed here. */
export interface NoteSummary {
  count: number;
  unread: boolean;
}

export type NoteSummaryItem = NotesSummariesRequest['items'][number];

/**
 * The summaries endpoint caps a body at 200 items (`NotesSummariesRequestSchema`)
 * but a keyset page holds up to FLOW_PAGE_SIZE (500) rows, so each page is cut
 * into slices of at most this many ids.
 */
export const NOTES_SUMMARIES_SLICE_SIZE = 200;

/**
 * The accessible name and tooltip for a notes count, shared by the row bubble and the header button.
 * `countUnknown` (no summary yet, but the record has notes) says just "Notes" rather than a wrong 0.
 */
export function notesCountLabels(count: number, unread: boolean, countUnknown: boolean): { aria: string; tip: string } {
  if (countUnknown) return { aria: 'Notes', tip: 'Notes' };
  const unreadSuffix = unread ? ', unread' : '';
  return { aria: `Notes, ${count}${unreadSuffix}`, tip: `Notes${count > 0 ? ` (${count})` : ''}${unreadSuffix}` };
}

/** Tables provide the batch Map; row bubbles read it. Empty by default, so a bubble outside a table shows the "no summary" state. */
export const NotesSummariesContext = createContext<ReadonlyMap<string, NoteSummary>>(new Map());

async function fetchSummaries(
  apiFetch: ReturnType<typeof useApi>['apiFetch'],
  items: NoteSummaryItem[],
): Promise<Map<string, NoteSummary>> {
  const body: NotesSummariesRequest = { items };
  const response = await fetchJson(apiFetch, NotesSummariesResponseSchema, '/api/flow/notes/summaries', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  return new Map(Object.entries(response).map(([key, v]) => [key, { count: v.count, unread: v.unread > 0 }]));
}

/**
 * Batch summaries for a Flow table: ONE query per keyset page slice, mirroring
 * `useContactStatePillsInternal` (`components/flow/contact/hooks.ts`). Do not
 * flatten `pages` and re-chunk: pages only append and never reorder, so
 * per-page slices keep query keys stable while later pages load.
 *
 * A slice that fails (HTTP error or schema-invalid body) contributes no
 * entries and shows no error UI; every other slice is untouched. All queries
 * sit under `FLOW_NOTES_SUMMARIES_KEY` so prefix invalidation refetches them.
 */
export function useNotesSummaries(pages: NoteSummaryItem[][]): Map<string, NoteSummary> {
  const { apiFetch } = useApi();
  const { enabled } = useNotesEnabled();

  const slices = useMemo(() => {
    const out: NoteSummaryItem[][] = [];
    for (const page of pages) {
      // Several rows can share one target (Signals: many signals, one person): each page lists it once.
      const seen = new Set<string>();
      const unique = page.filter(item => {
        const key = noteTargetKey(item);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      for (let i = 0; i < unique.length; i += NOTES_SUMMARIES_SLICE_SIZE) {
        out.push(unique.slice(i, i + NOTES_SUMMARIES_SLICE_SIZE).map(({ kind, id }) => ({ kind, id })));
      }
    }
    return out;
  }, [pages]);

  const queries = useQueries({
    queries: slices.map(items => ({
      queryKey: [...FLOW_NOTES_SUMMARIES_KEY, 'page', items.map(noteTargetKey).join(',')],
      enabled: enabled && items.length > 0,
      staleTime: Infinity,
      meta: { suppressErrorToast: true },
      queryFn: () => fetchSummaries(apiFetch, items),
    })),
  });

  return useMemo(() => {
    const merged = new Map<string, NoteSummary>();
    // A disabled flag hides cached data too.
    if (!enabled) return merged;
    for (const q of queries) {
      if (!q.data) continue;
      for (const [key, value] of q.data) merged.set(key, value);
    }
    return merged;
  }, [queries, enabled]);
}

/** Single-target summary for headers and bands. Same root as the batch keys, so one invalidation covers both. */
export function useNotesSummary(target: Pick<NoteTarget, 'kind' | 'id'>): NoteSummary | undefined {
  const { apiFetch } = useApi();
  const { enabled } = useNotesEnabled();
  const key = noteTargetKey(target);
  const query = useQuery({
    queryKey: [...FLOW_NOTES_SUMMARIES_KEY, 'one', key],
    enabled,
    staleTime: Infinity,
    meta: { suppressErrorToast: true },
    queryFn: () => fetchSummaries(apiFetch, [{ kind: target.kind, id: target.id }]),
  });
  return enabled ? query.data?.get(key) : undefined;
}
